//import "./safetyforms.css";
import { getUrl } from "aws-amplify/storage";
import { snakeCase } from 'lodash';
import moment from 'moment';
import { useContext, useState } from "react";
import "react-dropdown/style.css";
import { useForm } from "react-hook-form";
import { FaDownload, FaEdit, FaTimes, FaTrash } from "react-icons/fa";
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { modifyDocument, removeDocument } from "../common/slices/document";
import { pathNames } from "../constants";
import { BlackButton, ButtonBase, LightButton } from "./Buttons";
import Divider from "./Divider";
import { DocumentAttributes, DocumentProperties } from "./DocumentAttributes";
import { Container } from './Attribute';
import FormCheckbox from "./FormCheckbox";
import FormTextArea from "./FormTextArea";
import FormTextInput from "./FormTextInput";
import ModalConfirm from "./ModalConfirm";
import Row from "./Row";
import Sidebar from "./Sidebar";
import { H1, P3 } from "./Typography";
import { ErrorContext } from "../ErrorContext";

const UpdateDocumentForm = ({ document, onDone }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isValid, isSubmitting }
    } = useForm({
        defaultValues: document
    });
    const formProps = { control, errors };
    const fillable = watch('fillable');

    const onSubmit = async (data) => {
        try {
            const updated = await dispatch(modifyDocument(data));
            if (updated) onDone();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to update document.", forSupport: ex.message });
        }
    }

    return (
        <Container style={{ gap: 0 }}>
            <P3>Attributes</P3>
            <Row>
                <FormTextInput {...formProps} name="name" label="Name" placeholder="Enter form name" rules={{ required: true }} />
                {fillable && <FormTextInput {...formProps} name="daysUntilFinalized" label="Days Until Finalized" placeholder="Enter number of days (0-14)" rules={{ required: true, pattern: /^(1[0-4]|[0-9])$/i }} />}
                <FormCheckbox {...formProps} name="fillable" label="Fillable" containerStyle={{ flex: 0 }} />
            </Row>
            <FormTextArea {...formProps} name="summary" label="Summary" placeholder="Enter description" rows="7" />
            <BlackButton onClick={handleSubmit(onSubmit)} disabled={!isValid || isSubmitting}>Save</BlackButton>
            <LightButton onClick={onDone}>Cancel</LightButton>
        </Container>
    )
}


const SidebarDocument = ({ document }) => {
    const errorContext = useContext(ErrorContext);
    const nav = useNavigate();
    const dispatch = useDispatch();
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const onDeleteDocument = async () => {
        try {
            await dispatch(removeDocument(document));
            setShowDelete(false);
            nav(`${pathNames.SAFETYFORMS}/manage`);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to delete document", forSupport: ex.message });
        }
    }

    const onDownloadClick = async () => {
        try {
            const url = await getUrl({ path: `public/${document?.pdfKey}`, options: { contentDisposition: `attachment; filename=${snakeCase(document.name)}_${moment(document.createdAt).format("MM-DD-YYYY-hhmm")}.pdf` } });
            window.open(url.url, "_blank");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get signed URL for document", forSupport: ex.message });
        }
    }

    return (
        <Sidebar>
            <Row>
                <H1 style={{ flex: 1 }}>Form</H1>
                <ButtonBase style={{ padding: "0.5rem" }} onClick={() => nav(`${pathNames.SAFETYFORMS}/manage`)} disabled={showEdit}>
                    <FaTimes size={"14px"} color="#000" />
                </ButtonBase>
            </Row>
            <Row>
                <ButtonBase style={{ padding: "0.5rem" }} onClick={onDownloadClick}>
                    <FaDownload size={"14px"} color="#000" />
                </ButtonBase>
                <Divider.Vertical />
                <ButtonBase style={{ padding: "0.5rem" }} onClick={() => setShowEdit(true)}>
                    <FaEdit size={"14px"} color="#000" />
                </ButtonBase>
                <ButtonBase style={{ padding: "0.5rem" }} onClick={() => setShowDelete(true)}>
                    <FaTrash size={"14px"} color="#000" />
                </ButtonBase>
            </Row>
            {
                showEdit ? <UpdateDocumentForm document={document} onDone={() => setShowEdit(false)} /> : <DocumentAttributes document={document} />
            }
            <DocumentProperties document={document} />

            {showDelete && <ModalConfirm
                description={`Deleting <i>${document?.name}</i> will permanently delete the form from your library.  This cannot be undone.`}
                title="Confirm Delete"
                onConfirm={onDeleteDocument}
                onClose={() => setShowDelete(false)}
            />}
        </Sidebar>
    )
}

export default SidebarDocument;